import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { BarChart3, ChevronDown, ChevronRight, Trash2 } from "lucide-react";

const MESI_LABELS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

const formatCurrency = (value) => {
  if (value === undefined || value === null) return "0 €";
  if (value === 0) return "0 €";
  if (Math.abs(value) >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M €`;
  }
  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(0)}k €`;
  }
  return `${value.toLocaleString("it-IT", { maximumFractionDigits: 0 })} €`;
};

export function BudgetPreventivoTable({
  viewMode,
  activeTab,
  categorie,
  dataMatrix,
  totali,
  expandedCategories,
  toggleCategory,
  editingCell,
  setEditingCell,
  cellValue,
  setCellValue,
  handleSaveCell,
  handleDelete
}) {
  const tipoPositivo = activeTab === "economico" ? "ricavo" : "entrata";

  const renderCategorySection = (categorieFiltered, isPositive) => {
    const colorClass = isPositive ? "text-green-700" : "text-red-700";
    const bgClass = isPositive ? "bg-green-100/50" : "bg-red-100/50";
    
    return categorieFiltered.map(cat => (
      <React.Fragment key={cat.id}>
        <TableRow className={`${cat.colore} hover:bg-muted/30 cursor-pointer`} onClick={() => toggleCategory(cat.id)}>
          <TableCell className={`sticky left-0 ${cat.colore} z-10`}>
            <div className="flex items-center gap-2">
              {expandedCategories[cat.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <span className="text-sm font-medium">{cat.label}</span>
            </div>
          </TableCell>
          {dataMatrix[cat.id]?.mesi.map((val, mese) => (
            <TableCell key={mese} className="text-center text-sm p-1"
              onClick={(e) => { 
                if (viewMode === "preventivo") {
                  e.stopPropagation(); 
                  setEditingCell({ categoria: cat.id, mese, tipo: cat.tipo, conto: null }); 
                  setCellValue(val || ""); 
                }
              }}
            >
              {editingCell?.categoria === cat.id && editingCell?.mese === mese && !editingCell?.conto ? (
                <Input type="number" value={cellValue} onChange={(e) => setCellValue(e.target.value)}
                  onBlur={() => handleSaveCell(null, mese, cat.id, cat.tipo)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveCell(null, mese, cat.id, cat.tipo)}
                  className="h-9 w-28 text-sm font-mono text-right px-2 mx-auto" autoFocus
                />
              ) : (
                <span className={val > 0 ? colorClass : "text-muted-foreground"}>
                  {val > 0 ? formatCurrency(val) : "-"}
                </span>
              )}
            </TableCell>
          ))}
          <TableCell className={`text-right font-medium ${colorClass}`}>{formatCurrency(dataMatrix[cat.id]?.totale)}</TableCell>
          {viewMode === "preventivo" && <TableCell></TableCell>}
        </TableRow>
        
        {/* Dettaglio per conto */}
        {expandedCategories[cat.id] && Object.values(dataMatrix[cat.id]?.conti || {}).map(conto => (
          <TableRow key={conto.conto_id} className="bg-white/50 text-xs">
            <TableCell className="sticky left-0 bg-white/50 z-10 pl-8">
              <span className="text-muted-foreground">{conto.conto_codice} {conto.conto_nome}</span>
            </TableCell>
            {conto.mesi.map((val, mese) => (
              <TableCell key={`${conto.conto_id}-${mese}`} className="text-center p-1"
                onClick={(e) => {
                  if (viewMode === "preventivo") {
                    e.stopPropagation();
                    setEditingCell({ categoria: cat.id, mese, tipo: cat.tipo, conto: conto.conto_id });
                    setCellValue(val || "");
                  }
                }}
              >
                {editingCell?.categoria === cat.id && editingCell?.mese === mese && editingCell?.conto === conto.conto_id ? (
                  <Input type="number" value={cellValue} onChange={(e) => setCellValue(e.target.value)}
                    onBlur={() => handleSaveCell(conto.conto_id, mese, cat.id, cat.tipo)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveCell(conto.conto_id, mese, cat.id, cat.tipo)}
                    className="h-8 w-28 text-sm font-mono text-right px-2 mx-auto" autoFocus
                  />
                ) : (
                  <span className={val > 0 ? "" : "text-muted-foreground"}>
                    {val > 0 ? formatCurrency(val) : "-"}
                  </span>
                )}
              </TableCell>
            ))}
            <TableCell className="text-right">{formatCurrency(conto.totale)}</TableCell>
            {viewMode === "preventivo" && (
              <TableCell>
                {conto.entries.length > 0 && (
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDelete(conto.entries[0].id)}>
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                )}
              </TableCell>
            )}
          </TableRow>
        ))}
      </React.Fragment>
    ));
  };

  return (
    <Card data-testid="preventivo-table">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          {viewMode === "preventivo" ? "Budget Preventivo" : "Consuntivo"} - Vista Mensile
        </CardTitle>
        {viewMode === "preventivo" && (
          <CardDescription>Clicca su una cella per modificare. Espandi le categorie per vedere il dettaglio per conto.</CardDescription>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="w-full">
          <div className="min-w-[1600px]">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-[250px] sticky left-0 bg-muted/50 z-10">Categoria / Conto</TableHead>
                  {MESI_LABELS.map((m, i) => (
                    <TableHead key={m} className="text-center w-[120px] text-xs">{m}</TableHead>
                  ))}
                  <TableHead className="text-right w-[100px] font-bold">TOTALE</TableHead>
                  {viewMode === "preventivo" && <TableHead className="w-[50px]"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Sezione Ricavi/Entrate */}
                <TableRow className="bg-green-100/50 font-semibold">
                  <TableCell className="sticky left-0 bg-green-100/50 z-10">
                    {activeTab === "economico" ? "RICAVI" : "ENTRATE"}
                  </TableCell>
                  {totali.mensili.map((m, i) => (
                    <TableCell key={`entrate-${i}`} className="text-center text-green-700 text-sm">{formatCurrency(m.entrate)}</TableCell>
                  ))}
                  <TableCell className="text-right font-bold text-green-700">{formatCurrency(totali.entrate)}</TableCell>
                  {viewMode === "preventivo" && <TableCell></TableCell>}
                </TableRow>
                
                {renderCategorySection(categorie.filter(c => c.tipo === tipoPositivo), true)}

                {/* Sezione Costi/Uscite */}
                <TableRow className="bg-red-100/50 font-semibold">
                  <TableCell className="sticky left-0 bg-red-100/50 z-10">
                    {activeTab === "economico" ? "COSTI" : "USCITE"}
                  </TableCell>
                  {totali.mensili.map((m, i) => (
                    <TableCell key={`uscite-${i}`} className="text-center text-red-700 text-sm">{formatCurrency(m.uscite)}</TableCell>
                  ))}
                  <TableCell className="text-right font-bold text-red-700">{formatCurrency(totali.uscite)}</TableCell>
                  {viewMode === "preventivo" && <TableCell></TableCell>}
                </TableRow>
                
                {renderCategorySection(categorie.filter(c => c.tipo !== tipoPositivo), false)}

                {/* Riga Saldo */}
                <TableRow className={`font-bold ${totali.saldo >= 0 ? 'bg-green-200/50' : 'bg-red-200/50'}`}>
                  <TableCell className={`sticky left-0 ${totali.saldo >= 0 ? 'bg-green-200/50' : 'bg-red-200/50'} z-10`}>
                    {activeTab === "economico" ? "RISULTATO OPERATIVO" : "SALDO DI CASSA"}
                  </TableCell>
                  {totali.mensili.map((m, i) => (
                    <TableCell key={`saldo-${i}`} className={`text-center ${m.saldo >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatCurrency(m.saldo)}
                    </TableCell>
                  ))}
                  <TableCell className={`text-right font-bold ${totali.saldo >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatCurrency(totali.saldo)}
                  </TableCell>
                  {viewMode === "preventivo" && <TableCell></TableCell>}
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
